import { Sparkles, ArrowRight, Truck, Ticket, LogIn } from "lucide-react";
import { Link } from "react-router-dom";

import { splitDuration } from "@/lib/openingClock";

// Grand Opening countdown overlay — full-viewport, brand-red hero that shows
// the 8/29/2026 launch date until the visitor either taps into the raffle
// funnel or hits "Preview the site". Dismissal is stashed in sessionStorage
// only (Sprint 13.44) so the splash returns on every fresh browser visit —
// giving repeat traffic another chance to enter the raffle — but doesn't
// re-cover the signup/login page inside the same session after they click
// through.
//
// Sprint 13.60 — splash state is now owned by App.js (see
// `SPLASH_STORAGE_KEY`/`splashInitiallyDismissed`). Router + Landing lazy
// import don't mount until App flips splash off, so on pre-launch visits
// the browser never even downloads the Landing chunk, dropping mobile-4G
// LCP dramatically. This component is now purely presentational; it
// receives `msToOpen` + `onBypass` from App.
export const SPLASH_STORAGE_KEY = "bbb_grand_opening_bypassed_v1";
export const RAFFLE_INTENT_KEY = "bbb_raffle_intent";

/** Read the initial "should the splash be dismissed?" state from
 *  sessionStorage / URL. Called by App.js on first render. */
export function splashInitiallyDismissed() {
  if (typeof window === "undefined") return true;
  try {
    // Query-string escape hatch — used by playwright + shareable "skip"
    // links so QA never has to click through manually.
    if (window.location.search.includes("skipSplash=1")) {
      window.sessionStorage.setItem(SPLASH_STORAGE_KEY, "1");
      return true;
    }
    return window.sessionStorage.getItem(SPLASH_STORAGE_KEY) === "1";
  } catch (_e) {
    return false;
  }
}

export default function GrandOpeningSplash({ msToOpen, onBypass }) {
  const cd = splitDuration(msToOpen);

  const bypass = () => {
    try { window.sessionStorage.setItem(SPLASH_STORAGE_KEY, "1"); } catch (_e) { /* fine */ }
    if (typeof onBypass === "function") onBypass();
  };

  return (
    <div
      data-testid="grand-opening-splash"
      role="dialog"
      aria-modal="true"
      aria-label="Grand Opening announcement"
      className="fixed inset-0 z-[10000] bg-[var(--bb-red)] text-white overflow-y-auto"
      style={{ backgroundImage: "radial-gradient(ellipse at top, rgba(255,255,255,0.08), transparent 60%)" }}
    >
      <div className="min-h-full flex flex-col">
        <div className="px-6 sm:px-10 py-6 flex items-center justify-between border-b-2 border-white/20">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5" strokeWidth={2.5} />
            <span className="font-display text-lg uppercase tracking-widest">
              Bye-Bye Buckeye
            </span>
          </div>
          <button
            type="button"
            data-testid="grand-opening-bypass"
            onClick={bypass}
            className="hidden sm:inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest border-2 border-white/50 hover:border-white bg-transparent hover:bg-white/10 px-3 py-1.5 btn-snap"
          >
            Preview the site
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex-1 grid place-items-center px-6 sm:px-10 py-10">
          <div className="max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 border-2 border-white/60 bg-white/10 px-3 py-1 mb-6">
              <Sparkles className="w-4 h-4 text-yellow-300" strokeWidth={2.5} />
              <span className="text-[11px] font-bold uppercase tracking-[0.35em] text-yellow-300">
                Grand Opening
              </span>
            </div>
            <h1
              data-testid="grand-opening-date"
              className="font-display uppercase leading-none tracking-tight text-6xl sm:text-8xl lg:text-9xl"
            >
              8/29/2026
            </h1>
            <div className="mt-6 text-lg sm:text-2xl font-bold uppercase tracking-widest text-white/90">
              Bowling Green, Ohio
            </div>
            <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-white/80 leading-relaxed">
              Junk removal + roll-off dumpsters, doors officially open August 29th.
              Book your spot early &mdash; we&rsquo;re already loading up the schedule.
            </p>

            <div
              data-testid="grand-opening-countdown"
              className="mt-8 grid grid-cols-4 gap-2 sm:gap-4 max-w-lg mx-auto"
            >
              {[
                ["Days", cd.days],
                ["Hours", cd.hours],
                ["Min", cd.minutes],
                ["Sec", cd.seconds],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="border-2 border-white/60 bg-black/25 py-3 sm:py-4"
                >
                  <div className="font-display text-3xl sm:text-5xl leading-none tabular-nums">
                    {String(value).padStart(2, "0")}
                  </div>
                  <div className="mt-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-white/70">
                    {label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 max-w-md mx-auto">
              <RaffleAccountCTA onNavigate={bypass} msToOpen={msToOpen} />
            </div>

            <button
              type="button"
              data-testid="grand-opening-bypass-mobile"
              onClick={bypass}
              className="mt-6 sm:hidden inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest border-2 border-white/60 bg-transparent hover:bg-white/10 px-4 py-2 btn-snap"
            >
              Preview the site
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>

            <div className="mt-10 text-[11px] font-bold uppercase tracking-[0.3em] text-white/60">
              380-333-4721 · support@byebyebuckeye.com
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Splash raffle CTA (Sprint 13.43) — replaces the old phone/email "text me
// when you open" notify form. Instead of harvesting a lead into the launch
// waitlist we now steer the visitor into creating an account which double-
// counts as their raffle entry (one ticket per verified email/phone across
// all account signups). The two buttons stash a session flag so that after
// signup/login the app auto-POSTs `/api/account/raffle-entry` and toasts
// "You're entered". `onNavigate` (Sprint 13.44) fires *before* the router
// change so the splash dismisses itself for the rest of the session — the
// splash was still App-root-rendered, so without dismissing it would
// re-cover the signup/login page and swallow every click there.
function RaffleAccountCTA({ onNavigate, msToOpen }) {
  const handleClick = () => {
    try { sessionStorage.setItem(RAFFLE_INTENT_KEY, "grand-opening"); }
    catch (_e) { /* private mode — fine, banner still shows */ }
    if (typeof onNavigate === "function") onNavigate();
  };
  // Sprint 13.56 — Winner Reveal Countdown. Splash already surfaces the
  // opening-day countdown, but returning visitors were skimming past it —
  // adding a dedicated "winner drawn in" ticker under the raffle CTA makes
  // the deadline feel personal. Uses the same tick source (`msToOpen`) as
  // the big DHMS grid above so both stay in sync to the second.
  const cd = splitDuration(msToOpen);
  const parts = [];
  if (cd.days > 0) parts.push(`${cd.days}d`);
  if (cd.days > 0 || cd.hours > 0) parts.push(`${cd.hours}h`);
  parts.push(`${cd.minutes}m`);
  // Only include seconds when we're in the final-hour crunch — otherwise
  // "12h 04m 33s" is more noise than urgency.
  if (cd.days === 0 && cd.hours === 0) parts.push(`${cd.seconds}s`);
  const drawIn = parts.join(" ");
  return (
    <div
      data-testid="grand-opening-raffle-cta"
      className="border-2 border-white/80 bg-black/30 px-4 py-4 text-center"
    >
      <div className="inline-flex items-center gap-2 border-2 border-yellow-300 bg-yellow-300/10 px-2.5 py-1">
        <Ticket className="w-3.5 h-3.5 text-yellow-300" strokeWidth={2.5} />
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-300">
          Grand-Opening Raffle
        </span>
      </div>
      <div className="mt-3 font-display text-2xl sm:text-3xl uppercase leading-tight">
        Win a free truckload haul
      </div>
      <p className="mt-2 text-xs sm:text-sm text-white/80 leading-relaxed">
        Create your Bye-Bye Buckeye account before 8/29/2026 &mdash; every
        account is one ticket in the ribbon-cutting drawing. Winner announced
        opening morning.
      </p>
      {/* Live "winner drawn in Xd Yh Zm" ticker — climbs the urgency each
          time a returning visitor lands on the splash. */}
      <div
        data-testid="grand-opening-winner-countdown"
        className="mt-3 inline-flex items-center gap-2 border-2 border-yellow-300/60 bg-black/40 px-3 py-1.5"
        aria-live="polite"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-300">
          Winner drawn in
        </span>
        <span
          data-testid="grand-opening-winner-countdown-value"
          className="font-display text-base sm:text-lg leading-none tabular-nums text-white"
        >
          {drawIn}
        </span>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Link
          to="/account/signup?raffle=grand-opening"
          data-testid="grand-opening-raffle-signup"
          onClick={handleClick}
          className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 border-2 border-white bg-yellow-300 hover:bg-yellow-200 text-[var(--bb-ink)] font-bold uppercase tracking-widest text-xs btn-snap"
        >
          <Ticket className="w-3.5 h-3.5" strokeWidth={2.5} />
          Enter the Raffle
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
        </Link>
        <Link
          to="/account/login?raffle=grand-opening"
          data-testid="grand-opening-raffle-login"
          onClick={handleClick}
          className="inline-flex items-center justify-center gap-1.5 h-11 px-4 border-2 border-white/70 bg-transparent hover:bg-white/10 text-white font-bold uppercase tracking-widest text-[11px] btn-snap"
        >
          <LogIn className="w-3.5 h-3.5" strokeWidth={2.5} />
          I have an account
        </Link>
      </div>
      <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
        One entry per household · no purchase necessary
      </div>
    </div>
  );
}
